import React, { useEffect, useState } from 'react';
import { Button } from 'geniebook-ui/dist';
import {
  IPlanOverviewDetail,
  ISubjectSubscription,
  IUpdateRenewalReq,
  SubscriptionStatus,
} from '@models/interfaces/user.interface';
import {
  isInAppPurchase,
  priceWithCurrency,
} from '@components/subscription/overview/helper/PlanDetailHelper';
import {
  errorAtom,
  isExpired,
  selectedSubjectIdAtom,
  subscriptionInvoiceIdAtom,
  successAtom,
} from '@components/subscription/store/SubscriptionAtoms';
import { Player } from '@lottiefiles/react-lottie-player';
import button_loading from '@assets/animation/loading-dots.json';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import userServicesServices from '@services/GeniebookAPI/UserServices.services';
import { useAtomValue, useSetAtom } from 'jotai';
import moment from 'moment/moment';
import { canProceedToSelfServePayment } from '@components/subscription/helper/SubscriptionHelper';
import { DetailUser } from '@models/types/users.type';
import {
  GB_TUTOR_PILOT,
  GB_TUTOR_PILOT_BASIC,
} from '@components/subscription/helper/SubscriptionConstant';
import CancelConfirmationPopUp from '@components/subscription/overview/CancelConfirmationPopUp';
import { useAtom } from 'jotai';
import { useInvoiceStatus } from '@components/subscription/hooks/InvoiceStatusHook';
import UnsubscribeReasonPopUp from '@components/subscription/overview/UnsubscribeReasonPopUp';
import { subjectSubscriptionsQueryAtom } from '@atom/globalQueryAtom';

export interface SubscriptionActionButtonProps {
  planDetail: IPlanOverviewDetail;
  subscription: ISubjectSubscription;
  userDetail: DetailUser;
}

const SubscriptionActionButton = ({
  subscription,
  planDetail,
  userDetail,
}: SubscriptionActionButtonProps) => {
  const expiryDate = moment(subscription?.expiry_date + '+08:00').format(
    'D MMM YYYY',
  );
  const [loadingAction, setLoadingAction] = useState(false);
  const setSuccess = useSetAtom(successAtom);
  const setError = useSetAtom(errorAtom);
  const queryClient = useQueryClient();
  const [invoiceId, setInvoiceId] = useAtom(subscriptionInvoiceIdAtom);
  const subjectId = useAtomValue(selectedSubjectIdAtom);
  const invoiceStatus = useInvoiceStatus({
    subject_id: Number(subjectId),
    subscription_invoice_id: Number(invoiceId),
  });
  const updateRenewalMutation = useMutation({
    mutationFn: async (formData: IUpdateRenewalReq) => {
      return userServicesServices.updateRenewal(formData);
    },
    onMutate: () => {
      setLoadingAction(true);
    },
    onSuccess: ({ data }, variables) => {
      if (data.error) {
        setError(data.error_msg);
      } else {
        if (variables.auto_renewal_new_status === 1) {
          setSuccess(
            'Hooray! Cheers to being part of the Geniebook Family for another year!',
          );
        } else {
          setSuccess({
            message: 'Your subscription will end on ' + expiryDate + '.',
            states: 'neutral',
            buttonText: '',
            onTextClick: () => {},
          });
        }
        queryClient.invalidateQueries({ queryKey: ['plan-overview-detail'] });
        queryClient.invalidateQueries({ queryKey: ['subscriptionData'] });
        subscriptionQuery.refetch();
      }
    },
    onSettled: () => {
      setLoadingAction(false);
    },
  });
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showUnsubscribeReason, setShowUnsubscribeReason] = useState(false);
  const subscriptionQuery = useAtomValue(subjectSubscriptionsQueryAtom);

  useEffect(() => {
    if (invoiceStatus === 'success') {
      setSuccess('Your purchase is successful');
      queryClient.invalidateQueries({ queryKey: ['plan-overview-detail'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionData'] });
      subscriptionQuery.refetch();

      setInvoiceId(null);
    }
  }, [invoiceStatus]);

  const requestPaymentMutation = useMutation({
    mutationFn: async (subjectId: number) => {
      return userServicesServices.requestPaymentLink(subjectId);
    },
    onMutate: () => {
      setLoadingAction(true);
    },
    onSuccess: ({ data }) => {
      if (data.error) {
        setError(data.error_msg);
      } else {
        window.open(data.data.payment_link, '_blank');
        setInvoiceId(data.data.subscription_invoice_id);
      }
    },
    onSettled: () => {
      setLoadingAction(false);
    },
  });

  // clear the polling if any before leaving
  useEffect(() => {
    return () => {
      setInvoiceId(null);
    };
  }, []);

  function buttonText() {
    const idsToHide = [GB_TUTOR_PILOT, GB_TUTOR_PILOT_BASIC];
    const timePeriod =
      planDetail.billing_frequency > 1
        ? planDetail.billing_frequency +
          ' ' +
          planDetail.billing_period.toLowerCase() +
          's'
        : planDetail.billing_period.toLowerCase();

    if (
      isInAppPurchase(planDetail) ||
      planDetail.subscription_status ==
        SubscriptionStatus.subscriptionFailedChargeExpired ||
      planDetail.subscription_status ==
        SubscriptionStatus.subscriptionFailedChargeStillActive ||
      (planDetail.subscription_status ==
        SubscriptionStatus.subscriptionExpired &&
        subscription.auto_renewal) ||
      idsToHide.findIndex(id => id == planDetail.subscription_plan_id) !== -1
    ) {
      return null;
    }

    if (subscription.is_trial_plan || isExpired(subscription)) {
      return (
        'Upgrade to ' +
        planDetail.subscription_plan_name +
        ' - ' +
        priceWithCurrency(planDetail) +
        '/' +
        timePeriod
      );
    }

    if (subscription.auto_renewal) {
      return 'Cancel subscription';
    } else {
      if (
        planDetail.subscription_status == SubscriptionStatus.activeAutoRenewOff
      ) {
        return 'Reactivate subscription';
      }
    }

    return null;
  }

  function handleClickSubscribeAction() {
    if (loadingAction || invoiceStatus === 'in-progress') {
      return;
    }
    if (subscription.is_trial_plan || isExpired(subscription)) {
      buyPackage();
    } else if (subscription.auto_renewal) {
      // disableRenewal();
      setShowCancelConfirmation(true);
    } else {
      enableRenewal();
    }
  }

  function buyPackage() {
    if (!canProceedToSelfServePayment(subscription, planDetail, userDetail)) {
      window.open('https://geniebook.com/contact', '_blank');
    } else {
      requestPaymentMutation.mutate(subscription.subject_id);
    }
  }

  function enableRenewal() {
    updateRenewalMutation.mutate({
      subscription_id: planDetail.subscription_id!,
      auto_renewal_new_status: 1,
    });
  }

  function disableRenewal(reason: string) {
    updateRenewalMutation.mutate({
      subscription_id: planDetail.subscription_id!,
      auto_renewal_new_status: 0,
      remark: reason,
    });
  }

  function handleConfirmCancel() {
    setShowCancelConfirmation(false);
    setShowUnsubscribeReason(true);
  }

  function handleSubmitReason(reason: string) {
    disableRenewal(reason);
    setShowUnsubscribeReason(false);
  }

  if (!buttonText()) {
    return null;
  }

  return (
    <div className={'w-full pl-[2px]'}>
      <Button
        className={
          '!max-w-[calc(100%-2px)] !h-[56px] !font-extrabold hover:transform hover:!scale-[1.01] ' +
          (buttonText() === 'Cancel subscription' ? '!bg-alert-error' : '')
        }
        onClick={handleClickSubscribeAction}
      >
        {loadingAction || invoiceStatus === 'in-progress' ? (
          <Player
            autoplay={true}
            src={button_loading}
            loop={true}
            style={{ height: '32px' }}
          />
        ) : (
          buttonText()
        )}
      </Button>
      {showCancelConfirmation && (
        <CancelConfirmationPopUp
          onClose={() => setShowCancelConfirmation(false)}
          onConfirm={handleConfirmCancel}
          date={moment(planDetail.recurring.recurring_date + '+08:00').format(
            'D MMMM YYYY',
          )}
        />
      )}
      {showUnsubscribeReason && (
        <UnsubscribeReasonPopUp
          onClose={() => setShowUnsubscribeReason(false)}
          onSubmit={handleSubmitReason}
        />
      )}
    </div>
  );
};

export default SubscriptionActionButton;
